'use client';

import { twMerge } from '@tuic/tailwind-config';

import {
  Pagination
} from '../../../core-components/Pagination';

import { CarouselInterface } from './CarouselInterface';

import type {
  PaginationProps} from '../../../core-components/Pagination';
import type { FC, HTMLAttributes } from 'react';

export type CarouselDefaultPaginationProps = Omit<
  HTMLAttributes<HTMLElement>,
  'children'
> & {
  centered?: PaginationProps['centered'];
  i18n?: PaginationProps['i18n'];
  className?: string;
};

/**
 * Render the default carousel pagination in your carousel.
 *
 * > ⚠️ Important: This component is designed to function exclusively as a descendant of the `Carousel` component.
 *
 * ```tsx
 * <UniversalCarousel>
 *   <CarouselStage>
 *     <Image src="..." className="aspect-4/3" />
 *     <Image src="..." className="aspect-4/3" />
 *     <Image src="..." className="aspect-4/3" />
 *     // Add as many items as needed ...
 *   </CarouselStage>
 *   <CarouselDefaultPagination />
 * </UniversalCarousel>
 * ```
 */
export const CarouselDefaultPagination: FC<
  CarouselDefaultPaginationProps
> = attrs => {
  return (
    <CarouselInterface>
      {context => {
        const {
          visibleItems,
          visibleIndexes,
          lastIndex,
          scrollToIndex,
          stageWidth,
          stageScrollWidth,
        } = context;

        if (stageScrollWidth <= stageWidth) {
          return null; // No pagination needed if all items fit
        }

        const stepSize = visibleItems || 1; // Number of items per page
        const totalPages = Math.ceil((lastIndex + 1) / stepSize); // Total number of pages
        const firstVisibleIndex = Math.min(...visibleIndexes); // First currently visible item index
        const lastVisibleIndex = Math.max(...visibleIndexes); // Last currently visible item index

        // Calculate current page: Ensure correct page number on partial visibility
        const currentPage = Math.ceil((lastVisibleIndex + 1) / stepSize);

        // Boundaries for disabling prev/next buttons
        const isPrevDisabled = firstVisibleIndex <= 0;
        const isNextDisabled = lastVisibleIndex >= lastIndex;

        const handlePrev = () => {
          const targetIndex = Math.max(0, firstVisibleIndex - stepSize);
          scrollToIndex(targetIndex, 'start');
        };

        const handleNext = () => {
          const targetIndex = Math.min(
            lastVisibleIndex + 1,
            lastIndex - stepSize + 1,
          );
          scrollToIndex(targetIndex, 'start');
        };

        return (
          <Pagination
            {...attrs}
            total={totalPages}
            current={currentPage}
            onPrevious={isPrevDisabled ? undefined : handlePrev}
            onNext={isNextDisabled ? undefined : handleNext}
            forcePreviousClickable={!isPrevDisabled}
            forceNextClickable={!isNextDisabled}
            className={twMerge('mt-8', attrs.className)}
          />
        );
      }}
    </CarouselInterface>
  );
};
